import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertQuestionMappings,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = 'all'
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // ===== 跳題顯示條件 =====
  // q38 (16a) 矩陣 row_op: 1=沒有困難, 2=有一點困難, 3=很困難, 4=完全做不到
  //   → 有困難 = row<>1
  const q38AnyDifficulty =
    '({q38.1} <> 1) or ({q38.2} <> 1) or ({q38.3} <> 1) or ' +
    '({q38.4} <> 1) or ({q38.5} <> 1) or ({q38.6} <> 1) or ' +
    '({q38.7} <> 1) or ({q38.8} <> 1) or ({q38.9} <> 1) or ' +
    '({q38.10} <> 1)'
  const q38AnyDifficulty1to6 =
    '({q38.1} <> 1) or ({q38.2} <> 1) or ({q38.3} <> 1) or ' +
    '({q38.4} <> 1) or ({q38.5} <> 1) or ({q38.6} <> 1)'
  const q38AnyDifficulty7to10 =
    '({q38.7} <> 1) or ({q38.8} <> 1) or ({q38.9} <> 1) or ({q38.10} <> 1)'
  // q67/q83/q117/q150 需要情形矩陣：row=2 表示「需要」
  const anyRowNeed = (q, rows) =>
    rows.map((r) => `{${q}.${r}} = 2`).join(' or ')
  // 在學教育系列共同前置條件（q106 在學 + q107 階段 1-4 + q108 類型 1-4）
  const inEduFlow =
    '{q106} = 1 and ({q107} >= 1 and {q107} <= 4) and ({q108} >= 1 and {q108} <= 4)'
  // 36-51 免答條件：未滿 6 歲（q11 民國 110-115 年）或植物人（q16=9）
  // q11 empty 時也允許顯示，避免初始 render 時被誤藏
  const eligibleForADL =
    '(({q11} empty or {q11} < 110 or {q11} > 115) and {q16} <> 9)'
  // 129-140 免答條件：未滿 16 歲（q11 民國 100-115 年）或植物人（q16=9）
  const eligibleForWork =
    '(({q11} empty or {q11} < 100 or {q11} > 115) and {q16} <> 9)'

  const visibleIfConditionsInit = convertVisibleIf({
    // 70. q5(C1 回答情形)<>3(非大部分本人回答) → 跳至 q10，隱藏 q6-q9
    q6: '{q5} = 3',
    q7: '{q5} = 3',
    // 71. q7(C2 代答原因)<>1(允許代答條件) → 跳至 q9，隱藏 q8
    q8: '{q5} = 3 and {q7} = 1',
    q9: '{q5} = 3',
    // 1. q21=1/3/4/6 → 隱藏 q22(配偶國籍)；q22 只在已婚同居(2)/已婚分居(5) 顯示
    q22: '{q21} = 2 or {q21} = 5',
    // 2. q26 未勾配偶 → 隱藏 q27/q28
    q27: '{q26} anyof [1]',
    q28: '{q26} anyof [1]',
    // 3. q29<>1 → 隱藏 q30-q37
    // (q36-q44 同時被「未滿6歲/植物人」免答條件 eligibleForADL 控制)
    q30: '{q29} = 1',
    q31: '{q29} = 1',
    q32: '{q29} = 1',
    q33: '{q29} = 1',
    q34: '{q29} = 1',
    q35: '{q29} = 1',
    q36: '{q29} = 1',
    // 4. q36 (15a) 各選項 → 顯示 q37 (15b) 對應列
    q37: '{q29} = 1 and ({q36} anyof [1, 2, 3, 4, 5, 6, 7, 8, "other"])',
    'q37.row1': '{q29} = 1 and ({q36} anyof [1])',
    'q37.row2': '{q29} = 1 and ({q36} anyof [2])',
    'q37.row3': '{q29} = 1 and ({q36} anyof [3])',
    'q37.row4': '{q29} = 1 and ({q36} anyof [4])',
    'q37.row5': '{q29} = 1 and ({q36} anyof [5])',
    'q37.row6': '{q29} = 1 and ({q36} anyof [6])',
    'q37.row7': '{q29} = 1 and ({q36} anyof [7])',
    'q37.row8': '{q29} = 1 and ({q36} anyof [8])',
    'q37.row9': '{q29} = 1 and ({q36} anyof ["other"])',
    // 67. 未滿6歲或植物人 → 隱藏 q38/q39 (原本無條件，新增免答 gating)
    q38: eligibleForADL,
    q39: eligibleForADL,
    // 5. q39=2(都沒有困難) → 隱藏 q40-q44；q39 為訪員查核，未填時 (q39<>2 true) 仍允許顯示
    // 6+7. q38 矩陣某列<>1(有困難) → 顯示 q40 對應列 / q41(1-6項) / q42(7-10項)
    q40: '{q39} <> 2 and (' + q38AnyDifficulty + ') and ' + eligibleForADL,
    'q40.row1': '{q39} <> 2 and {q38.1} <> 1 and ' + eligibleForADL,
    'q40.row2': '{q39} <> 2 and {q38.2} <> 1 and ' + eligibleForADL,
    'q40.row3': '{q39} <> 2 and {q38.3} <> 1 and ' + eligibleForADL,
    'q40.row4': '{q39} <> 2 and {q38.4} <> 1 and ' + eligibleForADL,
    'q40.row5': '{q39} <> 2 and {q38.5} <> 1 and ' + eligibleForADL,
    'q40.row6': '{q39} <> 2 and {q38.6} <> 1 and ' + eligibleForADL,
    'q40.row7': '{q39} <> 2 and {q38.7} <> 1 and ' + eligibleForADL,
    'q40.row8': '{q39} <> 2 and {q38.8} <> 1 and ' + eligibleForADL,
    'q40.row9': '{q39} <> 2 and {q38.9} <> 1 and ' + eligibleForADL,
    'q40.row10': '{q39} <> 2 and {q38.10} <> 1 and ' + eligibleForADL,
    q41: '{q39} <> 2 and (' + q38AnyDifficulty1to6 + ') and ' + eligibleForADL,
    q42: '{q39} <> 2 and (' + q38AnyDifficulty7to10 + ') and ' + eligibleForADL,
    // 68. q41=1 且 q42=1 (1-6/7-10 都沒人幫) → 隱藏 q43(16e 主要幫忙者)
    q43: '{q39} <> 2 and ({q41} <> 1 or {q42} <> 1) and ' + eligibleForADL,
    q44: '{q39} <> 2 and ' + eligibleForADL,
    // 8. q45=1(可獨立) → 隱藏 q46-q48
    q46: '{q45} = 2 or {q45} = 3',
    q47: '{q45} = 2 or {q45} = 3',
    // 9. q47=9(沒有家人) → 隱藏 q48
    q48: '({q45} = 2 or {q45} = 3) and {q47} <> 9',
    // 10. q49=8/9(拒答 or 非本人回答) → 隱藏 q50/q51
    q50: '{q49} <> 8 and {q49} <> 9',
    // 11. q50=8(沒有次要) → 隱藏 q51
    q51: '{q49} <> 8 and {q49} <> 9 and {q50} <> 8',
    // 12. q52=1(不需要定期就醫) → 隱藏 q53/q54
    q53: '{q52} = 2',
    q54: '{q52} = 2',
    // 13. q56=1(自己前往) → 隱藏 q57(陪同者)
    q57: '{q56} = 2',
    // 14. q59<>2(沒做過/不知道) → 隱藏 q60(頻率)
    q60: '{q59} = 2',
    // 15. q61=1(沒使用輔具) → 隱藏 q62-q65
    q62: '{q61} = 2',
    q63: '{q61} = 2',
    // 16+17. q63=1 隱藏 q65; q63=2 隱藏 q64
    q64: '{q61} = 2 and {q63} = 1',
    q65: '{q61} = 2 and {q63} = 2',
    // 18. q67(25b需要)各列=2 → 顯示 q68(25c利用) 對應列
    q68: anyRowNeed('q67', [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    'q68.row1': '{q67.1} = 2',
    'q68.row2': '{q67.2} = 2',
    'q68.row3': '{q67.3} = 2',
    'q68.row4': '{q67.4} = 2',
    'q68.row5': '{q67.5} = 2',
    'q68.row6': '{q67.6} = 2',
    'q68.row7': '{q67.7} = 2',
    'q68.row8': '{q67.8} = 2',
    'q68.row9': '{q67.9} = 2',
    'q68.row10': '{q67.10} = 2',
    // 19. q68 各列=2(需要,未利用) → 顯示 q69-q78 對應「未利用原因」
    q69: '{q68.1} = 2',
    q70: '{q68.2} = 2',
    q71: '{q68.3} = 2',
    q72: '{q68.4} = 2',
    q73: '{q68.5} = 2',
    q74: '{q68.6} = 2',
    q75: '{q68.7} = 2',
    q76: '{q68.8} = 2',
    q77: '{q68.9} = 2',
    q78: '{q68.10} = 2',
    // 20+21. q79=11(皆不需要)/q80=11(沒有次要)
    q80: '{q79} <> 11',
    q81: '{q79} <> 11 and {q80} <> 11',
    // 22. q83(27b需要) → q84(27c利用) 各列
    q84: anyRowNeed('q83', [1, 2, 3, 4, 5]),
    'q84.row1': '{q83.1} = 2',
    'q84.row2': '{q83.2} = 2',
    'q84.row3': '{q83.3} = 2',
    'q84.row4': '{q83.4} = 2',
    'q84.row5': '{q83.5} = 2',
    // 23. q84 各列=2 → q85-q89 對應未利用原因
    //   注意 JSON 順序：q85=居家(row1), q88=社區(row2), q86=機構(row3), q87=經濟(row4), q89=就業(row5)
    q85: '{q84.1} = 2',
    q86: '{q84.3} = 2',
    q87: '{q84.4} = 2',
    q88: '{q84.2} = 2',
    q89: '{q84.5} = 2',
    // 24+25. q90=6(皆不需要)/q91=6(沒有次要)
    q91: '{q90} <> 6',
    q92: '{q90} <> 6 and {q91} <> 6',
    // 26. q93=11/12/13/14 → 隱藏 q94-q97
    q94: '({q93} >= 1 and {q93} <= 10) or {q93} = "other"',
    // 27. q94=13(沒有次要) → 隱藏 q95
    q95: '(({q93} >= 1 and {q93} <= 10) or {q93} = "other") and {q94} <> 13',
    q96: '({q93} >= 1 and {q93} <= 10) or {q93} = "other"',
    q97: '({q93} >= 1 and {q93} <= 10) or {q93} = "other"',
    // 28+29. q98=13(不知道) → 隱藏 q99/q100；q99=13(沒有次要) → 隱藏 q100
    q99: '{q98} <> 13',
    q100: '{q98} <> 13 and {q99} <> 13',
    // 30. q101<>1(夠用) → 隱藏 q102-q104
    q102: '{q101} = 1',
    // 31+32. q102=9(不知道)/q103=9(沒有次要)
    q103: '{q101} = 1 and {q102} <> 9',
    q104: '{q101} = 1 and {q102} <> 9 and {q103} <> 9',
    // 33. q106=2(不在學) → 隱藏 q107-q115
    q107: '{q106} = 1',
    // 34. q107=5/6(大學/碩博士) → 隱藏 q108-q115
    q108: '{q106} = 1 and ({q107} >= 1 and {q107} <= 4)',
    // 35. q108=5/6(一般學校未接受特教/非學校型態) → 隱藏 q109-q115
    q109: inEduFlow,
    // q109(38a) 選(1)有需要調整 → 顯示 q110(38b)
    q110: inEduFlow + ' and {q109} = 1',
    q111: inEduFlow,
    // 36. q111(39) 勾「沒有使用過」(互斥) → 隱藏 q112(40)
    q112: inEduFlow + ' and ({q111} notcontains ["none"])',
    q113: inEduFlow,
    // 37. q113(41a) 選(1)有需求 → 顯示 q114(41b) 與 q115(42 通勤困難)
    q114: inEduFlow + ' and {q113} = 1',
    q115: inEduFlow + ' and {q113} = 1',
    // 38. q117(43b需要) → q118(43c利用) 各列
    q118: anyRowNeed('q117', [1, 2, 3, 4, 5, 6, 7]),
    'q118.row1': '{q117.1} = 2',
    'q118.row2': '{q117.2} = 2',
    'q118.row3': '{q117.3} = 2',
    'q118.row4': '{q117.4} = 2',
    'q118.row5': '{q117.5} = 2',
    'q118.row6': '{q117.6} = 2',
    'q118.row7': '{q117.7} = 2',
    // 39. q118 各列=2 → q119-q125 對應未利用原因
    q119: '{q118.1} = 2',
    q120: '{q118.2} = 2',
    q121: '{q118.3} = 2',
    q122: '{q118.4} = 2',
    q123: '{q118.5} = 2',
    q124: '{q118.6} = 2',
    q125: '{q118.7} = 2',
    // 40+41. q126=8(皆不需要)/q127=8(沒有次要)
    q127: '{q126} <> 8',
    q128: '{q126} <> 8 and {q127} <> 8',
    // 42+43. q129(45)=1/2(有工作) → q132; =4-10 → q140; =3 → q130
    // 74. 未滿16歲(q11 民國100-115)或植物人(q16=9) → 隱藏 q129-q140
    q129: eligibleForWork,
    //   q130(46) 顯示：q129=3(在找工作) 或 q129="other"
    q130: '({q129} = 3 or {q129} = "other") and ' + eligibleForWork,
    // 44+45. q130=1(能立刻開始) → 跳過 q131; q130=2 顯示 q131
    q131:
      '({q129} = 3 or {q129} = "other") and {q130} = 2 and ' + eligibleForWork,
    // q132-q137 只在 q129=1/2 顯示
    q132: '({q129} = 1 or {q129} = 2) and ' + eligibleForWork,
    q133: '({q129} = 1 or {q129} = 2) and ' + eligibleForWork,
    q134: '({q129} = 1 or {q129} = 2) and ' + eligibleForWork,
    q135: '({q129} = 1 or {q129} = 2) and ' + eligibleForWork,
    q136: '({q129} = 1 or {q129} = 2) and ' + eligibleForWork,
    q137: '({q129} = 1 or {q129} = 2) and ' + eligibleForWork,
    // 46. q137(51)=5(無酬家屬工作者) → 隱藏 q138/q139
    q138: '({q129} = 1 or {q129} = 2) and {q137} <> 5 and ' + eligibleForWork,
    q139: '({q129} = 1 or {q129} = 2) and {q137} <> 5 and ' + eligibleForWork,
    q140: eligibleForWork,
    // 47. q140(54)=5(幾乎都沒外出) → 隱藏 q141-q148
    q141: '{q140} <> 5',
    q142: '{q140} <> 5',
    // 48. q142(56a)=15(未使用交通工具) → 隱藏 q143/q144
    q143: '{q140} <> 5 and {q142} <> 15',
    // 49. q143(56b)=15(沒有次要)
    q144: '{q140} <> 5 and {q142} <> 15 and {q143} <> 15',
    q145: '{q140} <> 5',
    // 50. q145(57a)=10(不需要無障礙設施) → 隱藏 q146/q147
    q146: '{q140} <> 5 and {q145} <> 10',
    // 51. q146(57b)=10(沒有次要)
    q147: '{q140} <> 5 and {q145} <> 10 and {q146} <> 10',
    // 72. q145(57a)=10(不需要無障礙設施) → 跳至 q149，隱藏 q148(58 設施使用困難)
    q148: '{q140} <> 5 and {q145} <> 10',
    // 52. q150(59b需要) → q151(59c利用) 各列
    q151: anyRowNeed('q150', [1, 2, 3, 4, 5, 6, 7, 8]),
    'q151.row1': '{q150.1} = 2',
    'q151.row2': '{q150.2} = 2',
    'q151.row3': '{q150.3} = 2',
    'q151.row4': '{q150.4} = 2',
    'q151.row5': '{q150.5} = 2',
    'q151.row6': '{q150.6} = 2',
    'q151.row7': '{q150.7} = 2',
    'q151.row8': '{q150.8} = 2',
    // 53. q151 各列=2(需要,未利用) → 顯示 q152-q159 對應未利用原因
    q152: '{q151.1} = 2',
    q153: '{q151.2} = 2',
    q154: '{q151.3} = 2',
    q155: '{q151.4} = 2',
    q156: '{q151.5} = 2',
    q157: '{q151.6} = 2',
    q158: '{q151.7} = 2',
    q159: '{q151.8} = 2',
    // 54+55. q160(60a)=9(皆不需要) / q161(60b)=9(沒有次要)
    q161: '{q160} <> 9',
    q162: '{q160} <> 9 and {q161} <> 9',
    // 56. q163(61a)矩陣 某列<>3(非「沒有意願參與」) → 顯示 q164(61b) 對應列
    q164:
      '{q163.1} <> 3 or {q163.2} <> 3 or {q163.3} <> 3 or {q163.4} <> 3 or ' +
      '{q163.5} <> 3 or {q163.6} <> 3 or {q163.7} <> 3',
    'q164.row1': '{q163.1} <> 3',
    'q164.row2': '{q163.2} <> 3',
    'q164.row3': '{q163.3} <> 3',
    'q164.row4': '{q163.4} <> 3',
    'q164.row5': '{q163.5} <> 3',
    'q164.row6': '{q163.6} <> 3',
    'q164.row7': '{q163.7} <> 3',
    // 57. q167(64)=2(有申請) → 隱藏 q168(64a 尚未申請原因)
    q168: '{q167} = 1',
    // 58. q168 答完即跳至 q173；改寫為 q169-q172(64b/65/66/67) 只在有申請時顯示
    q169: '{q167} = 2',
    // 73. q169(64b 愛心卡頻率)=6(未使用) → 跳至 q173，隱藏 q170-q172(65/66/67)
    q170: '{q167} = 2 and {q169} <> 6',
    q171: '{q167} = 2 and {q169} <> 6',
    q172: '{q167} = 2 and {q169} <> 6',
    // 59. q173(68)=2(有) → 隱藏 q174(68a 沒使用原因)；q174 只在 q173=1 顯示
    q174: '{q173} = 1',
    // 60. q174 答完跳至 q177；改寫為 q175/q176 只在 q173=2(有使用) 顯示
    q175: '{q173} = 2',
    q176: '{q173} = 2',
    // 61. q177(69) 勾「非使用手語/聽打族群」(互斥) → 跳至 q182，隱藏 q178-q181
    q178: '{q177} notcontains ["none"]',
    // 62+63. q178(70)=3(不知道) → 跳至 q182；q178=2(知道未用) → 跳至 q180
    // 結果：q179(70a 幫助程度) 只在 q178=1(使用過) 顯示
    //      q180/q181(71/72 手譯場合/時數) 只在 q178 in [1,2] 顯示
    q179: '{q177} notcontains ["none"] and {q178} = 1',
    q180: '{q177} notcontains ["none"] and ({q178} = 1 or {q178} = 2)',
    q181: '{q177} notcontains ["none"] and ({q178} = 1 or {q178} = 2)',
    // 64. q184(74 人身安全事件)=2(沒有) → 跳至 q187，隱藏 q185/q186
    q185: '{q184} = 1',
    // 65. q185(75 求助對象) 勾「沒有求助」(互斥) → 跳至 q187，隱藏 q186
    q186: '{q184} = 1 and {q185} notcontains ["none"]',
    // 66. q182(a.是否本人)=2(否) → 隱藏 q190(80 限問本人 整體滿意度)
    q190: '{q182} = 1',
    // 69. q182=2(非本人) 或 q11 民國104-115年(未滿12歲) → 隱藏 q183(73 限12歲以上本人)
    q183: '{q182} = 1 and ({q11} empty or {q11} < 104 or {q11} > 115)',
  })
  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null
  const triggersConditionsInit = null
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  const endSurveyMsg = (survey) => {
    setCompletionMessage(
      `<div>感謝您的填答～歡迎轉發分享！<br><br>想參加更多問卷調查活動？手指點點就可以抽獎賺好康！<br>點擊下方按鈕加入 EZChoice APP<br>或複製下載連結 <a href="https://ezcapp.page.link/tAQv" target="_blank">https://ezcapp.page.link/tAQv</a><br>裡面還有更多填問卷拿購物金的活動唷～<br>各種好玩的問卷等你來玩！<br><br>想知道更多好康問卷、活動消息，請追蹤關注<br><a href="https://www.facebook.com/EasyCTRs" target="_blank">【輕鬆點點】粉絲專頁</a><br><br>加入我們的 <a href="https://lin.ee/HWETcqy" target="_blank">LINE@</a>，<br>隨時得到最新填問卷獲好禮的消息。<br><br><h4 style="text-align: center">請按「完成」前往下載 EZChoice 或填寫更多問卷</h4></div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving:
      requiresLogin.login || webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
